import { DEFAULT_ZOOM, ZOOMED_VEHICLE } from 'components/map/contants';
import type { BoundingBox, Position } from 'components/map/types';
import { setSeverity } from 'helper/setSeverity';
import { flattenV2Deviations } from 'helper/vehicleV2';
import { isEmpty } from 'remeda';

import {
    ConnectivityState,
    type MissionV2,
    MissionStateValue,
    MRMStateValue,
    type PositionInput,
    Severity,
    type VehicleV2,
} from 'API';

export const DEFAULT_POSITION: Readonly<PositionInput> = {
    latitude: 48.1351,
    longitude: 11.582,
    altitude: 123.7200275750379,
    heading: 0.0,
};

export const getInitialMapPosition = (): Position => ({
    lat: 48.1351,
    lng: 11.582,
});

export const getMapPosition = (vehicle?: VehicleV2) => vehicle?.position?.value?.position ?? DEFAULT_POSITION;

export const getMapZoom = (hasVehicle = false) => (hasVehicle ? ZOOMED_VEHICLE : DEFAULT_ZOOM);

export const calculateBoundingBox = (positions: Position[]): BoundingBox => {
    let pos = positions;
    if (isEmpty(positions)) {
        pos = [getInitialMapPosition()];
    }
    const allLats = pos.map(({ lat }) => lat);
    const allLngs = pos.map(({ lng }) => lng);

    const top = Math.max(...allLats);
    const bottom = Math.min(...allLats);
    const right = Math.max(...allLngs);
    const left = Math.min(...allLngs);

    const paddingY = (top - bottom) / 5;
    const paddingX = (right - left) / 5;
    return {
        top: top + paddingY,
        left: left - paddingX,
        right: right + paddingX * 1.5,
        bottom: bottom - paddingY,
    };
};

/**
 * Resolves the active mission of a vehicle across both backend regardless the feature flag.
 * Once preprod is retired, the `legacyMissionId` branch can go.
 */
export const resolveMissionForVehicle = (
    mission: MissionV2 | null | undefined,
    vehicle: VehicleV2 | undefined
): MissionV2 | undefined => {
    if (!mission?.id || !vehicle?.vin || mission.vin !== vehicle.vin) {
        return;
    }

    const legacyMissionId = vehicle.activeMissionId?.value?.data;

    return !legacyMissionId || legacyMissionId === mission.id ? mission : undefined;
};

export const setMarkerColor = (vehicle: VehicleV2) => {
    const severity = setSeverity(flattenV2Deviations(vehicle.deviations));
    const noCriticalDeviation =
        !severity || (severity !== Severity.FATAL && severity !== Severity.ERROR && severity !== Severity.WARNING);
    const isInMRMProcess =
        vehicle.mrmState?.value?.data?.value === MRMStateValue.EXECUTING_MRM ||
        vehicle.mrmState?.value?.data?.value === MRMStateValue.MRC ||
        vehicle.mrmState?.value?.data?.value === MRMStateValue.RELEASING_MRC;
    const isOffline =
        vehicle.connectivityStatus?.value?.state === ConnectivityState.OFFLINE ||
        vehicle.connectivityStatus?.value?.state === ConnectivityState.UNKNOWN;

    switch (true) {
        case severity === Severity.FATAL:
            return 'VehicleMapMarker--fatal';
        case severity === Severity.ERROR:
            return 'VehicleMapMarker--error';
        case severity === Severity.WARNING:
            return 'VehicleMapMarker--warning';
        case noCriticalDeviation && !isInMRMProcess && isOffline:
            return 'VehicleMapMarker--offline';
        case noCriticalDeviation && isInMRMProcess:
        default:
            return 'VehicleMapMarker';
    }
};

type RouteColorAssigned = {
    color: '#9AD0DC';
    borderColor: '#328092';
};
type RouteColorActive = {
    color: '#328092';
    borderColor: '#255F6C';
};

type RouteColorPaused = {
    color: '#A7AFBB';
    borderColor: '#4C5667';
};

type RouteColorCompleted = {
    color: '#71B564';
    borderColor: '#5C8D52';
};

type RouteColorFailed = {
    color: '#D90000';
    borderColor: '#B20000';
};

type RouteColorCanceled = {
    color: '#D90000';
    borderColor: '#B20000';
};

type RouteColor =
    | RouteColorAssigned
    | RouteColorActive
    | RouteColorPaused
    | RouteColorCompleted
    | RouteColorCanceled
    | RouteColorFailed;

/**
 * Returns the route color based on the mission state.
 *
 * @param {MissionStateValue} missionState - The current state of the mission.
 * @return {RouteColor} The color associated with the mission state.
 */
export const getRouteColor = (missionState: MissionStateValue | undefined): RouteColor => {
    const redRoute: RouteColorFailed = {
        color: '#D90000',
        borderColor: '#B20000',
    };
    const colorMapping: Record<MissionStateValue, RouteColor> = {
        [MissionStateValue.CANCELED]: redRoute,
        [MissionStateValue.COMPLETE]: { color: '#71B564', borderColor: '#5C8D52' },
        [MissionStateValue.EXECUTING]: { color: '#328092', borderColor: '#255F6C' },
        [MissionStateValue.FAILED]: redRoute,
        [MissionStateValue.LANDED]: { color: '#71B564', borderColor: '#5C8D52' },
        [MissionStateValue.PAUSED]: { color: '#A7AFBB', borderColor: '#4C5667' },
        [MissionStateValue.PAUSING]: { color: '#A7AFBB', borderColor: '#4C5667' },
        [MissionStateValue.READY_TO_RESUME]: {
            color: '#A7AFBB',
            borderColor: '#4C5667',
        },
        [MissionStateValue.READY_TO_LAUNCH]: {
            color: '#9AD0DC',
            borderColor: '#328092',
        },
        [MissionStateValue.RESET]: { color: '#328092', borderColor: '#255F6C' },
        [MissionStateValue.SCHEDULED]: { color: '#9AD0DC', borderColor: '#328092' },
        [MissionStateValue.UNKNOWN]: redRoute,

        // TODO: to be removed after backend is updated
        [MissionStateValue.ACTIVE]: { color: '#328092', borderColor: '#255F6C' }, // deprecated, for backward compatibility
        [MissionStateValue.ASSIGNED]: { color: '#9AD0DC', borderColor: '#328092' }, // deprecated, for backward compatibility
        [MissionStateValue.COMPLETED]: { color: '#71B564', borderColor: '#5C8D52' }, // deprecated, for backward compatibility
    };

    return missionState ? colorMapping[missionState] || redRoute : redRoute;
};
